// EditOvernightTaskDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Edit, Check } from 'lucide-react';
import { PRIORITY_OPTIONS } from '../constants';

/**
 * Dialog for editing a queued overnight task
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data { title, description, priority }
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {function} props.onSave - Callback when save is clicked
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const EditOvernightTaskDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  onSave,
  onCancel
}) => {
  const handleFieldChange = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Queued Task
          </DialogTitle>
          <DialogDescription>
            Modify this pending task before it runs
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Task Title *</Label>
            <Input
              data-testid="edit-overnight-task-title"
              value={formData.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="e.g., Generate Q1 Marketing Report"
            />
          </div>

          <div>
            <Label>Description *</Label>
            <Textarea
              data-testid="edit-overnight-task-description"
              value={formData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Describe what this task should accomplish..."
              rows={3}
            />
          </div>

          <div>
            <Label>Priority (1 = Highest, 10 = Lowest)</Label>
            <Select 
              value={String(formData.priority || 5)} 
              onValueChange={(val) => handleFieldChange('priority', parseInt(val))}
            >
              <SelectTrigger data-testid="edit-overnight-task-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onCancel}
            data-testid="edit-overnight-task-cancel"
          >
            Cancel
          </Button>
          <Button 
            onClick={onSave}
            disabled={!formData.title || !formData.description}
            data-testid="edit-overnight-task-save"
          >
            <Check className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditOvernightTaskDialog;
